/**
 * 閉合部件庫 — 第一批獸來源。
 * 形狀用程序化幾何區分；色鎖定江山繡像礦物色。
 */

import type { BeastForm, FlowerKind, FruitKind, PlantForm, TreeBaseId } from './types'

export const EMBROIDERY = {
  azurite: '#3d6b8a',
  malachite: '#4a7a5c',
  ochre: '#b07840',
  cinnabar: '#a83a32',
  clam: '#e8dcc8',
  ink: '#2a241c',
} as const

export type AnimalSourceId = 'ren' | 'yang' | 'ma' | 'hu'

export interface SourceDef {
  id: AnimalSourceId
  name: string
  /** 身／頭／尾主色 */
  body: string
  head: string
  tail: string
  accent: string
}

export const ANIMAL_SOURCES: SourceDef[] = [
  { id: 'ren', name: '人', body: EMBROIDERY.clam, head: EMBROIDERY.clam, tail: EMBROIDERY.ochre, accent: EMBROIDERY.ink },
  { id: 'yang', name: '羊', body: EMBROIDERY.clam, head: EMBROIDERY.ochre, tail: EMBROIDERY.clam, accent: EMBROIDERY.ink },
  { id: 'ma', name: '馬', body: EMBROIDERY.ochre, head: EMBROIDERY.ochre, tail: EMBROIDERY.ink, accent: EMBROIDERY.cinnabar },
  { id: 'hu', name: '虎', body: EMBROIDERY.ochre, head: EMBROIDERY.ochre, tail: EMBROIDERY.cinnabar, accent: EMBROIDERY.ink },
]

export const SOURCE_BY_ID = Object.fromEntries(ANIMAL_SOURCES.map(s => [s.id, s])) as Record<AnimalSourceId, SourceDef>

export function isAnimalSource(id: string): id is AnimalSourceId {
  return id in SOURCE_BY_ID
}

export const TEMPERAMENT_LABEL: Record<string, string> = {
  ignore: '不理',
  call: '鳴',
  flee: '避',
  approach: '近',
}

export const ENCOUNTER_LABEL: Record<string, string> = {
  appear: '現身',
  message: '短訊',
  trace: '留痕跡',
}

export const CLAY_TINTS: { id: string; name: string; hex: string | null }[] = [
  { id: 'none', name: '本色', hex: null },
  { id: 'bi', name: '碧', hex: EMBROIDERY.malachite },
  { id: 'qing', name: '青', hex: EMBROIDERY.azurite },
  { id: 'zhu', name: '朱', hex: EMBROIDERY.cinnabar },
  { id: 'zhe', name: '赭', hex: EMBROIDERY.ochre },
  { id: 'fen', name: '粉', hex: EMBROIDERY.clam },
  { id: 'mo', name: '墨', hex: EMBROIDERY.ink },
]

export function defaultBeastForm(): BeastForm {
  return {
    headScale: 1,
    headStretch: 1,
    snout: 1,
    earSpread: 1,
    bodyScale: 1,
    bodyLength: 1,
    tailScale: 1,
    tailCurl: 0.2,
    tint: null,
  }
}

export function defaultParts(): { slot: 'body' | 'head' | 'tail'; source: AnimalSourceId }[] {
  return [
    { slot: 'body', source: 'yang' },
    { slot: 'head', source: 'ren' },
    { slot: 'tail', source: 'hu' },
  ]
}

export type PlantSourceId = 'gui' | 'sang' | 'tong' | 'zhu' | 'he' | 'song' | 'lan' | 'tao'

export interface PlantSourceDef {
  id: PlantSourceId
  name: string
  trunk: string
  leaf: string
  flower: string
  accent: string
}

export const PLANT_SOURCES: PlantSourceDef[] = [
  { id: 'gui', name: '桂', trunk: EMBROIDERY.ochre, leaf: EMBROIDERY.malachite, flower: EMBROIDERY.ochre, accent: EMBROIDERY.ink },
  { id: 'sang', name: '桑', trunk: EMBROIDERY.ink, leaf: EMBROIDERY.malachite, flower: EMBROIDERY.cinnabar, accent: EMBROIDERY.ochre },
  { id: 'tong', name: '桐', trunk: EMBROIDERY.clam, leaf: EMBROIDERY.malachite, flower: EMBROIDERY.clam, accent: EMBROIDERY.ink },
  { id: 'zhu', name: '竹', trunk: EMBROIDERY.malachite, leaf: EMBROIDERY.malachite, flower: EMBROIDERY.clam, accent: EMBROIDERY.ink },
  { id: 'he', name: '荷', trunk: EMBROIDERY.malachite, leaf: EMBROIDERY.malachite, flower: EMBROIDERY.cinnabar, accent: EMBROIDERY.clam },
  { id: 'song', name: '松', trunk: EMBROIDERY.ink, leaf: EMBROIDERY.azurite, flower: EMBROIDERY.ochre, accent: EMBROIDERY.ink },
  { id: 'lan', name: '蘭', trunk: EMBROIDERY.malachite, leaf: EMBROIDERY.malachite, flower: EMBROIDERY.cinnabar, accent: EMBROIDERY.clam },
  { id: 'tao', name: '桃', trunk: EMBROIDERY.ochre, leaf: EMBROIDERY.malachite, flower: EMBROIDERY.cinnabar, accent: EMBROIDERY.clam },
]

export const PLANT_BY_ID = Object.fromEntries(PLANT_SOURCES.map(s => [s.id, s])) as Record<PlantSourceId, PlantSourceDef>

export function isPlantSource(id: string): id is PlantSourceId {
  return id in PLANT_BY_ID
}

export function defaultPlantParts(): { slot: 'trunk' | 'leaf' | 'flower'; source: PlantSourceId }[] {
  return [
    { slot: 'trunk', source: 'song' },
    { slot: 'leaf', source: 'song' },
    { slot: 'flower', source: 'tao' },
  ]
}

export const TREE_BASES: { id: TreeBaseId; name: string; preset: 'pine_medium' | 'oak_medium' | 'willow_medium' | 'aspen_medium' | 'bush_1' }[] = [
  { id: 'song', name: '松', preset: 'pine_medium' },
  { id: 'tong', name: '桐', preset: 'oak_medium' },
  { id: 'liu', name: '柳', preset: 'willow_medium' },
  { id: 'zhu', name: '竹', preset: 'aspen_medium' },
  { id: 'guan', name: '灌', preset: 'bush_1' },
]

export const LEAF_TINTS: { id: string; name: string; hex: string }[] = [
  { id: 'bi', name: '碧', hex: EMBROIDERY.malachite },
  { id: 'qing', name: '青', hex: EMBROIDERY.azurite },
  { id: 'zhu', name: '朱', hex: EMBROIDERY.cinnabar },
  { id: 'zhe', name: '赭', hex: EMBROIDERY.ochre },
  { id: 'fen', name: '粉', hex: EMBROIDERY.clam },
  { id: 'mo', name: '墨', hex: EMBROIDERY.ink },
]

export const FLOWER_KINDS: { id: FlowerKind; name: string; hex: string }[] = [
  { id: 'none', name: '無', hex: EMBROIDERY.clam },
  { id: 'tao', name: '桃', hex: EMBROIDERY.cinnabar },
  { id: 'lan', name: '蘭', hex: '#7a4a8a' },
  { id: 'he', name: '荷', hex: EMBROIDERY.clam },
]

export const FRUIT_KINDS: { id: FruitKind; name: string; hex: string }[] = [
  { id: 'none', name: '無', hex: EMBROIDERY.ochre },
  { id: 'zao', name: '棗', hex: '#6a2a22' },
  { id: 'chi', name: '赤實', hex: EMBROIDERY.cinnabar },
  { id: 'jin', name: '金丸', hex: '#c4a050' },
]

export function defaultPlantForm(): PlantForm {
  return {
    base: 'song',
    seed: 3,
    leafTint: EMBROIDERY.malachite,
    flower: 'tao',
    fruit: 'zao',
    glow: false,
  }
}
